// components/habits/habit/habit.component.jsx
import React, { useEffect, useRef, useState } from "react";

import EmojiPicker from "../../emoji/emojiPicker";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";
import RetroButton from "../../retro/button/retro-button.component";

import {
  db,
  createNewHabit,
  updateHabit,
  deleteHabit,
  updateHabitHistory,
  getHabitReminders,
  syncHabitReminders,
} from "../../../firebase/firebase.utils";

import { RRule } from "rrule";
import moment from "moment";

import Recurring from "../../recurring/recurring.component";

import InteractiveHeatmap from "../../interactive-heatmap/InteractiveHeatmap";
import LineChart from "../../line-chart/LineChart";

import { BsPlusLg } from "react-icons/bs";
import { TfiLink, TfiUnlink } from "react-icons/tfi";

import DatePicker from "react-datepicker";

import {
  productivityHabits,
  growthHabits,
  healthHabits,
  homeHabits,
  financialHabits,
  socialHabits,
  selfcareHabits,
} from "./habit.templates";

import "./habit.styles.scss";

const CATEGORY_META = {
  Productivity: "⚡",
  Growth: "🌱",
  Health: "💪",
  Home: "🏠",
  Financial: "💰",
  Social: "🫶",
  Selfcare: "🧘",
};

const TEMPLATE_MAP = {
  Productivity: productivityHabits,
  Growth: growthHabits,
  Health: healthHabits,
  Home: homeHabits,
  Financial: financialHabits,
  Social: socialHabits,
  Selfcare: selfcareHabits,
};

const UNIT_GROUPS = [
  {
    label: "Quantity",
    options: ["time(s)", "count(s)", "page(s)"],
  },
  {
    label: "Measurement",
    options: ["cup(s)", "milliliter(s)"],
  },
  {
    label: "Duration",
    options: ["minute(s)", "pomodoro(s)", "hour(s)"],
  },
  {
    label: "Distance",
    options: ["meter(s)", "kilometer(s)", "feet", "step(s)", "mile(s)"],
  },
  {
    label: "Currency",
    options: ["dollar(s)", "euro(s)", "peso(s)"],
  },
];

const UNIT_ITEMS = UNIT_GROUPS.flatMap((group) => [
  {
    key: `group-${group.label}`,
    label: group.label,
    textValue: group.label,
    disabled: true,
    className: "habit-unit-group",
  },
  ...group.options.map((option) => ({
    key: option,
    label: option,
    textValue: option,
  })),
]);

const createDefaultRule = () =>
  new RRule({
    freq: RRule.DAILY,
    interval: 1,
  });

const getRule = (value) => {
  if (!value) return createDefaultRule();

  try {
    return RRule.fromString(value);
  } catch {
    return createDefaultRule();
  }
};

const sortRemindersByTime = (reminders) =>
  [...reminders].sort((a, b) => {
    const aTime = new Date(a.time);
    const bTime = new Date(b.time);

    return (
      aTime.getHours() * 60 +
      aTime.getMinutes() -
      (bTime.getHours() * 60 + bTime.getMinutes())
    );
  });

const getReminderMinutes = (value) => {
  const date = value instanceof Date ? value : new Date(value);

  return date.getHours() * 60 + date.getMinutes();
};

const isReminderTimeTaken = (reminders, time, ignoreIndex = null) => {
  if (!time) return false;

  const minutes = getReminderMinutes(time);

  return reminders.some(
    (reminder, index) =>
      index !== ignoreIndex && getReminderMinutes(reminder.time) === minutes,
  );
};

const getNextAvailableReminderTime = (reminders, interval = 15) => {
  const candidate = new Date();

  candidate.setSeconds(0, 0);

  const nextMinutes =
    Math.ceil((candidate.getMinutes() + 1) / interval) * interval;

  candidate.setMinutes(nextMinutes);

  // Search up to one full day.
  for (let i = 0; i < 24 * (60 / interval); i++) {
    if (!isReminderTimeTaken(reminders, candidate)) {
      return new Date(candidate);
    }

    candidate.setMinutes(candidate.getMinutes() + interval);
  }

  return null;
};

export default function Habit({
  currentUser,
  habit,
  habitIcon,
  setHabitIcon,
  habitName,
  setHabitName,
  relatedSpaceId,
  relatedStationId,
  setRefRRule,
  step,
  setStep,
  steps,
  setSteps,
  unit,
  setUnit,
  handleCloseHabit,
  habitmodalMode,
  selectedDate,
}) {
  const initialRule = getRule(habit?.refrrule);

  const [isOpen, setIsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [habitHistory, setHabitHistory] = useState([]);
  const [isLinked, setIsLinked] = useState("");

  const [activeHistoryTab, setActiveHistoryTab] = useState("heatmap");

  const [initialRRule, setInitialRRule] = useState(initialRule);
  const [modifiedRRule, setModifiedRRule] = useState(initialRule);

  const [rruleString, setRruleString] = useState(initialRule.toString());

  const [ruleHumanText, setRuleHumanText] = useState(initialRule.toText());

  const [reminders, setReminders] = useState([]);

  const templateScrollRef = useRef(null);
  const emojiButtonRef = useRef(null);

  const templateDragRef = useRef({
    pressed: false,
    dragging: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  });

  const habitGoal = Math.max(0, Number(steps) || 0);

  useEffect(() => {
    setActiveHistoryTab("heatmap");
    if (habitmodalMode !== "edit" || !habit?.id) {
      setReminders([]);
      return;
    }

    if (habit.refrrule) {
      const rule = getRule(habit.refrrule);

      setInitialRRule(rule);
      setModifiedRRule(rule);
      setRruleString(rule.toString());
      setRuleHumanText(rule.toText());
    }

    configureAlreadySavedReminders();
  }, [habit?.id, habitmodalMode]);

  useEffect(() => {
    if (habitmodalMode !== "edit" || !habit?.id || !currentUser?.uid) {
      setHabitHistory([]);
      return;
    }

    const unsub = db
      .collection("users")
      .doc(currentUser.uid)
      .collection("habits")
      .doc(habit.id)
      .onSnapshot((doc) => {
        const history = Array.isArray(doc.data()?.history)
          ? doc.data().history
          : [];

        setHabitHistory(
          [...history].sort((a, b) => (a.date > b.date ? 1 : -1)),
        );
      });

    return unsub;
  }, [habit?.id, habitmodalMode, currentUser?.uid]);

  useEffect(() => {
    if (!modifiedRRule) return;

    const ruleString = modifiedRRule.toString();

    setRruleString(ruleString);

    setRuleHumanText(modifiedRRule.toText());

    setRefRRule(ruleString);
  }, [modifiedRRule, setRefRRule]);

  const configureAlreadySavedReminders = async () => {
    if (!habit?.id || !currentUser?.uid) return;

    try {
      const loaded = await getHabitReminders(currentUser.uid, habit.id);

      setReminders(loaded);
    } catch (error) {
      console.error("Error loading habit reminders:", error);

      setReminders([]);
    }
  };

  const closeTemplates = () => {
    setTemplatesOpen(false);
  };

  const handleTemplateSelect = (template) => {
    setHabitName(template.name);
    setHabitIcon(template.icon);
    setStep(template.step);
    setSteps(template.steps);
    setUnit(template.unit);

    if (template.rrule) {
      const rule = getRule(template.rrule);

      setInitialRRule(rule);
      setModifiedRRule(rule);
      setRefRRule(template.rrule);
    }

    closeTemplates();
  };

  const onSelectEmoji = (emojiObject) => {
    setHabitIcon(emojiObject.native);

    setIsOpen(false);
  };

  const handlesetEmojiOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen((open) => !open);
  };

  const handleCellPress = async (date) => {
    await handleHabitMarkDone(date);
  };

  const handleHabitMarkDone = async (selectedDate) => {
    if (!habit?.id) return;

    const incrementalValue = Number(step) / Number(steps);

    const historyArray = Array.isArray(habitHistory) ? [...habitHistory] : [];

    const findIndex = historyArray.findIndex(
      (item) => item.date === selectedDate,
    );

    if (findIndex === -1) {
      historyArray.push({
        date: selectedDate,
        checked: Math.min(1, incrementalValue),
      });
    } else if (Number(historyArray[findIndex].checked) < 1) {
      const newCheckedValue = Math.min(
        1,
        Number(historyArray[findIndex].checked) + incrementalValue,
      );

      historyArray.splice(findIndex, 1, {
        date: selectedDate,
        checked: Math.round(newCheckedValue * 100) / 100,
      });
    } else {
      historyArray.splice(findIndex, 1, {
        date: selectedDate,
        checked: 0,
      });
    }

    try {
      await updateHabitHistory(currentUser.uid, habit.id, historyArray);

      setHabitHistory(historyArray);
    } catch (error) {
      console.error("Error updating habit history:", error);
    }
  };

  const normalizeReminderTime = (selected) => {
    if (!selected) return null;

    const now = new Date();

    const candidate = new Date(now);

    candidate.setHours(selected.getHours(), selected.getMinutes(), 0, 0);

    if (candidate <= now) {
      candidate.setDate(candidate.getDate() + 1);
    }

    return candidate;
  };

  const handleAddReminder = () => {
    if (reminders.length >= 5) return;

    setReminders((current) => {
      const time = getNextAvailableReminderTime(current);

      if (!time) return current;

      return sortRemindersByTime([
        ...current,
        {
          time,
        },
      ]);
    });
  };

  const handleReminderTimeChange = (index, selected) => {
    const time = normalizeReminderTime(selected);

    if (!time) return;

    if (isReminderTimeTaken(reminders, time, index)) {
      return;
    }

    setReminders((current) =>
      sortRemindersByTime(
        current.map((reminder, reminderIndex) =>
          reminderIndex === index
            ? {
                ...reminder,
                time,
              }
            : reminder,
        ),
      ),
    );
  };

  const handleRemoveReminder = (index) => {
    setReminders((current) =>
      current.filter((_, reminderIndex) => reminderIndex !== index),
    );
  };

  const handleCreateHabit = async (e) => {
    e?.preventDefault();

    const habitId = await createNewHabit(
      currentUser.uid,
      habitName,
      habitIcon,
      relatedSpaceId,
      relatedStationId,
      rruleString,
      step,
      steps,
      [
        {
          date: selectedDate,
          checked: 0,
        },
      ],
      unit,
    );

    await syncHabitReminders(currentUser.uid, habitId, reminders, {
      title: `${habitIcon} - ${habitName}`,
      rrule: rruleString || null,
      unit,
      step,
    });

    handleCloseHabit();
  };

  const handleUpdateHabit = async (e) => {
    e?.preventDefault();

    await updateHabit(
      currentUser.uid,
      habit.id,
      habitName,
      habitIcon,
      relatedSpaceId,
      relatedStationId,
      rruleString,
      step,
      steps,
      undefined, // preserve existing history
      unit,
    );

    await syncHabitReminders(currentUser.uid, habit.id, reminders, {
      title: `${habitIcon} - ${habitName}`,
      rrule: rruleString || null,
      unit,
      step,
    });

    handleCloseHabit();
  };

  const handleDeleteHabit = async (e) => {
    e?.preventDefault();

    if (!habit?.id) return;

    await deleteHabit(currentUser.uid, habit.id);

    handleCloseHabit();
  };

  const onRRuleChange = (rule) => {
    setModifiedRRule(typeof rule === "string" ? getRule(rule) : rule);
  };

  const handleTemplateWheel = (e) => {
    const el = templateScrollRef.current;

    if (!el || el.scrollWidth <= el.clientWidth) {
      return;
    }

    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

    if (!delta) return;

    const maxScroll = el.scrollWidth - el.clientWidth;

    const next = Math.max(0, Math.min(maxScroll, el.scrollLeft + delta));

    if (next !== el.scrollLeft) {
      e.preventDefault();
      el.scrollLeft = next;
    }
  };

  const handleTemplatePointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) {
      return;
    }

    const el = templateScrollRef.current;

    if (!el) return;

    templateDragRef.current = {
      pressed: true,
      dragging: false,
      moved: false,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
    };
  };

  const handleTemplatePointerMove = (e) => {
    const el = templateScrollRef.current;

    const drag = templateDragRef.current;

    if (!el || !drag.pressed) {
      return;
    }

    const distance = e.clientX - drag.startX;

    if (Math.abs(distance) > 4) {
      if (!drag.dragging) {
        drag.dragging = true;

        el.classList.add("is-dragging");

        el.setPointerCapture?.(e.pointerId);
      }

      drag.moved = true;

      el.scrollLeft = drag.scrollLeft - distance;
    }
  };

  const stopTemplateDrag = (e) => {
    const el = templateScrollRef.current;

    const drag = templateDragRef.current;

    if (!el) return;

    drag.pressed = false;

    if (drag.dragging) {
      drag.dragging = false;

      el.classList.remove("is-dragging");

      if (el.hasPointerCapture?.(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    }

    setTimeout(() => {
      templateDragRef.current.moved = false;
    }, 0);
  };

  const blockClickAfterDrag = (e) => {
    if (!templateDragRef.current.moved) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    templateDragRef.current.moved = false;
  };

  return (
    <div className="habit">
      <div className="habit-backdrop" onClick={handleCloseHabit} />

      <div className="habit-content">
        <div className="habit-topbar">
          <button
            type="button"
            className={`habit-templates-btn ${templatesOpen ? "active" : ""}`}
            onClick={() => setTemplatesOpen((open) => !open)}
          >
            ✨ Templates
          </button>

          <button
            type="button"
            className="habit__exit__button"
            onClick={handleCloseHabit}
            aria-label="Close habit"
          >
            <BsPlusLg />
          </button>
        </div>

        {templatesOpen && (
          <div className="habit-template-layer" onClick={closeTemplates}>
            <div
              className="habit-template-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                ref={templateScrollRef}
                className="habit-template-categories"
                onWheel={handleTemplateWheel}
                onPointerDown={handleTemplatePointerDown}
                onPointerMove={handleTemplatePointerMove}
                onPointerUp={stopTemplateDrag}
                onPointerCancel={stopTemplateDrag}
                onClickCapture={blockClickAfterDrag}
                onDragStart={(e) => e.preventDefault()}
              >
                {Object.entries(TEMPLATE_MAP).map(([category, templates]) => (
                  <DropdownMenu
                    key={category}
                    className="habit-template-dropdown"
                    triggerClassName="habit-template-category-trigger"
                    menuClassName="habit-template-dropdown-menu"
                    customBtn={
                      <div className="habit-template-category-content">
                        <span>{CATEGORY_META[category]}</span>

                        <strong>{category}</strong>

                        <small>⌄</small>
                      </div>
                    }
                    hasCaret={false}
                    openOn="hover"
                    closeDelay={200}
                    direction="auto"
                    align="start"
                    gap={5}
                    minWidth={190}
                    maxWidth={260}
                    maxHeight={320}
                    itemHeight={42}
                    items={templates.map((template) => ({
                      key: template.id || template.name,

                      label: template.name,

                      textValue: template.name,

                      icon: (
                        <span className="habit-template-menu-emoji">
                          {template.icon}
                        </span>
                      ),

                      template,
                    }))}
                    onSelect={(_, item) => handleTemplateSelect(item.template)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <form className="habit-form" onSubmit={(e) => e.preventDefault()}>
          <div className="habit-profile">
            <button
              ref={emojiButtonRef}
              type="button"
              className="HabitEmojiPick"
              onClick={handlesetEmojiOpen}
            >
              <span>{habitIcon || habit?.icon || "❔"}</span>
            </button>

            {isOpen && (
              <EmojiPicker
                floating
                anchorRef={emojiButtonRef}
                onEmojiSelect={onSelectEmoji}
                onClickOutside={() => setIsOpen(false)}
              />
            )}

            <div className="habit-profile-main">
              <input
                value={habitName}
                placeholder="Enter habit name"
                onChange={(e) => setHabitName(e.target.value)}
                className="habitnameinput"
              />

              {!!ruleHumanText && (
                <p className="habit-rule-text">{ruleHumanText}</p>
              )}
            </div>
          </div>

          <div className="habit-scroll">
            {habitmodalMode === "edit" && (
              <div className="habit-history">
                <details>
                  <summary>
                    <h3>Habit History</h3>
                  </summary>
                  <div style={{ display: "flex", gap: "20px" }}>
                    <button
                      type="button"
                      onClick={() => setActiveHistoryTab("heatmap")}
                    >
                      Heatmap
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveHistoryTab("linechart")}
                    >
                      Line chart
                    </button>
                  </div>

                  <div className="activitycal-container">
                    {activeHistoryTab === "heatmap" && (
                      <InteractiveHeatmap
                        data={habitHistory}
                        onCellPress={handleCellPress}
                      />
                    )}

                    {activeHistoryTab === "linechart" && (
                      <LineChart
                        data={habitHistory}
                        valueAccessor={(item) =>
                          (Number(item.checked) || 0) * habitGoal
                        }
                        unit={unit}
                        goalPerDay={habitGoal}
                      />
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* <div className="habit-form-header">
              <h3>Habit</h3>

              {isLinked === "" ? (
                <button
                  type="button"
                  onClick={() => setIsLinked("beingset")}
                  aria-label="Link project"
                >
                  <TfiLink />
                </button>
              ) : (
                <div className="habit-form-header-link">
                  <p>Coming feature 😊</p>

                  <select defaultValue="">
                    <option value="" disabled>
                      Link a Project
                    </option>

                    <option>Project 1</option>

                    <option>Project 2</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => setIsLinked("")}
                    aria-label="Cancel project link"
                  >
                    <TfiUnlink />
                  </button>
                </div>
              )}
            </div> */}

            <div className="habitsettings">
              <div className="habitoptions">
                <Recurring
                  initialRRule={initialRRule}
                  onRRuleChange={onRRuleChange}
                />
              </div>

              <div className="habit-reminders-row">
                <span className="habit-small-label">Remind me at</span>

                <div className="habit-reminders-list">
                  {reminders.map((reminder, index) => (
                    <div
                      className="habit-reminder-time"
                      key={reminder.id || `new-${index}`}
                    >
                      <DatePicker
                        selected={reminder.time}
                        onChange={(selected) =>
                          handleReminderTimeChange(index, selected)
                        }
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={5}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        excludeTimes={reminders
                          .filter((_, reminderIndex) => reminderIndex !== index)
                          .map((item) => {
                            const source = new Date(item.time);

                            const date = new Date();

                            date.setHours(
                              source.getHours(),
                              source.getMinutes(),
                              0,
                              0,
                            );

                            return date;
                          })}
                        withPortal
                        portalId="habit-time-picker-portal"
                        className="habit-reminder-time-input"
                      />

                      <button
                        type="button"
                        className="habit-reminder-remove"
                        onClick={() => handleRemoveReminder(index)}
                        aria-label="Remove reminder"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {reminders.length < 5 && (
                    <button
                      type="button"
                      className="habit-reminder-add"
                      onClick={handleAddReminder}
                    >
                      + Reminder
                    </button>
                  )}
                </div>
              </div>

              <div className="habitoptions">
                <label className="habit-small-label">My goal is</label>

                <input
                  type="number"
                  min={1}
                  className="inverval_number habit-goal-input"
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                />

                <DropdownMenu
                  items={UNIT_ITEMS}
                  selectedKey={unit}
                  onSelect={(key) => setUnit(key)}
                  triggerLabel={unit}
                  direction="auto"
                  align="center"
                  minWidth={150}
                  maxWidth={210}
                  maxHeight={280}
                  itemHeight={36}
                  triggerClassName="habit-unit-trigger"
                  menuClassName="habit-unit-menu"
                />

                <p className="habit-small-label">in habit day</p>
              </div>

              <div className="habitoptions">
                <label>Check</label>

                <input
                  type="number"
                  min={1}
                  max={steps}
                  className="inverval_number"
                  value={step}
                  onChange={(e) => setStep(e.target.value)}
                />

                <p>{unit} at a time</p>
              </div>
            </div>
          </div>

          <div className="habitbtns">
            {habitmodalMode === "edit" && (
              <RetroButton onClick={handleDeleteHabit}>Delete</RetroButton>
            )}

            <RetroButton
              onClick={
                habitmodalMode === "edit"
                  ? handleUpdateHabit
                  : handleCreateHabit
              }
            >
              {habitmodalMode === "edit" ? "Update" : "Save"}
            </RetroButton>
          </div>
        </form>
      </div>
    </div>
  );
}
